import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, ExternalLink, FileText, Flag, MessageCircle, SearchX, ShieldCheck } from "lucide-react";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { ReferralComposer } from "@/components/ReferralComposer";
import { SavedFacilityButton } from "@/components/board/SavedFacilityButton";
import { PublicShell } from "@/components/layout/PublicShell";
import { ListingCard } from "@/components/ListingCard";
import { Button, ButtonLink, EmptyState, Notice, Panel, SectionHead, Status, TextAreaField, TextField } from "@/components/system";
import { CrisisNotice } from "@/components/CrisisNotice";
import { useAudience } from "@/contexts/AudienceContext";
import { PATIENT_GUIDE } from "@/lib/patient-guide";
import { sendProfileTraffic, type TrafficEvent } from "@/lib/profile-traffic";
import { isPsychiatrySpecialty } from "@/lib/crisis-notice";
import { WaitReportForm } from "@/components/WaitReportForm";
import { SEO, specialistSeoMetadata } from "@/lib/seo";
import {
  bulkBillingLabel,
  submitCorrection,
  usePublicDirectory,
  useSpecialistProfile,
  type Facility,
  type FacilityAddressJson,
} from "@/lib/public-directory";
import { track } from "@/lib/analytics";
import {
  acceptingLabel,
  availabilityFreshnessCopy,
  referralMethodLabel,
  waitBucketLabel,
  type Availability,
} from "@/lib/availability";
import {
  buildPhysicianStructuredData,
  cleanPracticeName,
  hasPublishedReferral,
  practitionerFor,
  specialistBreadcrumbs,
  type SpecialistSeoInput,
} from "@/lib/structured-data";
import { breadcrumbStructuredData } from "@/lib/local-seo";
import { submitPublicLead } from "@/lib/public-contact";
import { slugifySpecialty } from "@/lib/specialty-slug";
import { specialtyContent } from "@/lib/specialty-content";
import { fullGpReportedLabel } from "@/lib/wait-reports";

/**
 * Public specialist profile page.
 * Route: /:citySlug/directory/specialist/:slug
 *
 * Data comes from GET /v1/public/:citySlug/directory/specialist/:slug.
 * This page only renders fields present in the public profile response.
 */

type AvailabilityTone = "available" | "limited" | "closed" | "unconfirmed";

type ExtendedAddressJson = FacilityAddressJson & {
  accepting_referrals?: string | boolean | null;
  availability_note?: string | null;
  wait_time?: string | null;
  wait_time_days?: number | string | null;
  routine_wait_time?: string | null;
  routine_wait_time_days?: number | string | null;
  soon_wait_time?: string | null;
  soon_wait_time_days?: number | string | null;
  urgent_wait_time?: string | null;
  urgent_wait_time_days?: number | string | null;
  referral_instructions?: string | string[] | null;
  referral_requirements?: string | string[] | null;
  referral_note?: string | null;
  clinical_interests?: string[] | string | null;
  special_interests?: string[] | string | null;
  telehealth?: boolean | string | null;
  accepts_bulk_bill?: boolean | string | null;
  verified?: boolean | string | null;
  claimed?: boolean | string | null;
  gmaps_url?: string | null;
  google_place_id?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
};

interface AvailabilitySummary {
  label: string;
  detail: string;
  tone: AvailabilityTone;
}

interface PracticeFlag {
  label: string;
  value: string;
  tone?: "teal" | "amber" | "coral" | "muted";
}

export default function SpecialistProfilePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { city } = useCity();
  const citySlug = city.slug;

  const { data, isLoading, isError } = useSpecialistProfile(citySlug, slug);
  const specialist = data?.specialist;
  const addressJson = specialist?.address_json as ExtendedAddressJson | undefined;
  const trackedProfileId = useRef<string | null>(null);
  const auth = useAuth();
  const { isGpMode: isClinician } = useAudience();


  useEffect(() => {
    // Wait for Keycloak's silent check so signed-in clinicians are bucketed
    // correctly in the first-party traffic counts.
    if (!specialist || auth.isLoading || trackedProfileId.current === specialist.id) return;
    trackedProfileId.current = specialist.id;
    track("profile_view");
    sendProfileTraffic(citySlug, slug, "view", { clinician: isClinician });
  }, [specialist, auth.isLoading, citySlug, slug, isClinician]);

  const displayName = specialist
    ? formatSpecialistDisplayName(specialist.name, addressJson?.title)
    : "";
  const seoInput: SpecialistSeoInput | null =
    specialist && addressJson
      ? {
          slug,
          name: displayName,
          specialty: addressJson.specialty,
          suburb: addressJson.suburb,
          practice: addressJson.practice_name,
          phone: specialist.phone,
          website: specialist.website,
          addressLine: profileAddressLine(addressJson),
          street: cleanText(addressJson.street),
          postcode: cleanText(addressJson.postcode),
          focus: dedupeList([...textList(addressJson.sub_specialties), ...textList(addressJson.clinical_interests)]),
          hasReferral: hasPublishedReferral(specialist.availability),
          cityName: data?.city.name ?? city.name,
        }
      : null;
  const profileMetadata = seoInput ? specialistSeoMetadata(seoInput) : null;

  return (
    <>
      {/* Only emit head tags once the profile has resolved: a loading or
          error state must not replace the prerendered title/description
          with generic text (SEO audit report 94). */}
      {seoInput && profileMetadata ? (
        <SEO
          title={profileMetadata.title}
          description={profileMetadata.description}
          path={profileMetadata.path}
          noindex={profileMetadata.noindex}
          follow
          ogType="profile"
          jsonLd={[
            buildPhysicianStructuredData(seoInput),
            breadcrumbStructuredData(specialistBreadcrumbs(seoInput, seoInput.cityName)),
          ]}
        />
      ) : !isLoading && !isError ? (
        <SEO
          title="Specialist not found · PulseBoard"
          description="This specialist listing may have been removed or the URL is incorrect."
          path={`/directory/specialist/${slug}`}
          noindex
        />
      ) : null}

      <PublicShell>
        {isLoading ? (
          <ProfileLoading />
        ) : isError || !specialist || !addressJson ? (
          <ProfileNotFound />
        ) : (
          <ProfileContent
            specialist={specialist}
            addressJson={addressJson}
            cityName={data?.city.name ?? city.name}
            citySlug={citySlug}
            slug={slug}
            displayName={displayName}
          />
        )}
      </PublicShell>
    </>
  );
}

function ProfileContent({
  specialist,
  addressJson,
  cityName,
  citySlug,
  slug,
  displayName,
}: {
  specialist: Facility;
  addressJson: ExtendedAddressJson;
  cityName: string;
  citySlug: string;
  slug: string;
  displayName: string;
}) {
  const { isAuthenticated, hasAnyRole } = useAuth();
  const canMessageRooms = isAuthenticated && hasAnyRole("gp", "specialist");
  const { isGpMode } = useAudience();
  const sendTraffic = (event: TrafficEvent) => sendProfileTraffic(citySlug, slug, event, { clinician: isGpMode });
  const sourceLabel = sourceDisplayName(addressJson.source);
  const sourceDateLabel = formatSourceDate(addressJson.source_date);
  const addressLine = profileAddressLine(addressJson);
  const availability = availabilityDisplay(specialist.availability);
  const clinicalInterests = clinicalInterestList(addressJson);
  const practiceFlags = practiceFlagList(addressJson);
  const statusMeta = verifiedStatus(addressJson);
  const [detailsCopied, setDetailsCopied] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const specialty = cleanText(addressJson.specialty);
  const practiceName = cleanPracticeName(addressJson.practice_name) ?? cleanPracticeName(specialist.practiceGroup?.name) ?? null;
  const suburb = cleanText(addressJson.suburb);
  const crumbs = specialistBreadcrumbs({ slug, name: displayName, specialty }, cityName);
  const hasReferral = Boolean(specialist.availability && hasReferralDetails(specialist.availability));
  const mapsUrl = cleanText(addressJson.gmaps_url);
  // Empty sections collapse into one line instead of three placeholder
  // panels (SEO audit report 28: thin profiles read as boilerplate).
  const notPublished = [
    clinicalInterests.length ? null : "clinical interests",
    specialist.availability ? null : "availability",
    hasReferral ? null : "referral requirements",
  ].filter((item): item is string => Boolean(item));
  const roleLine = [specialty ? practitionerFor(specialty) : null, practiceName, suburb]
    .filter(Boolean)
    .join(" · ");
  const referralTemplateInput = {
    specialistName: displayName,
    practiceName: practiceName ?? undefined,
    address: addressLine,
    phone: specialist.phone,
    fax: specialist.availability?.referralFax ?? cleanText(addressJson.fax),
    email: specialist.availability?.referralEmail ?? cleanText(addressJson.email),
    referralMethod: specialist.availability?.referralMethod
      ? referralMethodLabel(specialist.availability.referralMethod)
      : null,
    referralNotes:
      specialist.availability?.referralNotes ??
      cleanText(textList(addressJson.referral_requirements ?? addressJson.referral_instructions).join("; ")) ??
      cleanText(addressJson.referral_note),
  };

  return (
    <div className="ds-container">
      <div className="ds-page-head" style={{ paddingBottom: 24 }}>
        <ol className="ds-crumbs" aria-label="Breadcrumb">
          {crumbs.slice(0, -1).map((crumb) => (
            <li key={crumb.path}><Link to={crumb.path}>{crumb.name}</Link></li>
          ))}
        </ol>
      </div>

      <div className="ds-stack" style={{ gap: 16 }}>
        {isPsychiatrySpecialty(specialty) ? <CrisisNotice /> : null}

        <article className="ds-profile" aria-labelledby="profile-name">
          <div className="ds-profile__head">
            <div className="ds-profile__who">
              {specialty ? <p className="ds-eyebrow ds-eyebrow--teal">{specialty}</p> : null}
              <h1 id="profile-name" className="ds-display-1 ds-profile__name">{displayName}</h1>
              {roleLine ? <p className="ds-lead">{capitalise(roleLine)}</p> : null}
              {clinicalInterests.length > 0 ? (
                <ul className="ds-listing__tags" aria-label="Focus areas">
                  {clinicalInterests.map((item) => <li key={item} className="ds-tag">{item}</li>)}
                </ul>
              ) : null}
              <p className="ds-fine ds-profile__provenance">
                {statusMeta.verified ? <ShieldCheck aria-hidden="true" /> : <FileText aria-hidden="true" />}
                <span>{statusMeta.label}</span>
                <span>Source: {sourceLabel}</span>
                <span>{sourceDateLabel ? `Checked ${sourceDateLabel}` : "Check date not recorded"}</span>
              </p>
            </div>

            <aside className="ds-profile__avail" aria-label="Referral availability">
              <Status tone={availability.tone === "available" ? "ok" : availability.tone === "limited" ? "urgent" : availability.tone === "closed" ? "warn" : "neutral"}>
                {availability.tone === "unconfirmed" ? "Availability not confirmed" : availability.label}
              </Status>
              {specialist.availability ? (
                <dl className="ds-dl">
                  <div><dt>Typical wait</dt><dd>{waitBucketLabel(specialist.availability.waitBucket) ?? "Not published"}</dd></div>
                </dl>
              ) : null}
              <p className="ds-fine">
                {specialist.availability ? availabilityFreshnessCopy(specialist.availability) : "Contact the practice before referring."}
              </p>
              {specialist.gpReported ? <p className="ds-fine">{fullGpReportedLabel(specialist.gpReported)}</p> : null}
            </aside>
          </div>

          <ContactFacts specialist={specialist} addressJson={addressJson} addressLine={addressLine} mapsUrl={mapsUrl} onContact={sendTraffic} />

          <div className="ds-profile__actions">
            <Button
              onClick={() => {
                void copyText(referralDetailsText(specialist, addressJson, displayName)).then((copied) => {
                  setDetailsCopied(copied);
                  if (copied) sendTraffic("referral_copy");
                  if (copied) window.setTimeout(() => setDetailsCopied(false), 2500);
                });
              }}
            >
              <Copy aria-hidden="true" />
              {detailsCopied ? "Referral details copied" : "Copy referral details"}
            </Button>
            {isGpMode ? (
              <Button
                variant="secondary"
                aria-expanded={composerOpen}
                aria-controls="referral-composer"
                onClick={() => {
                  if (!composerOpen) sendTraffic("referral_draft");
                  setComposerOpen((open) => !open);
                }}
              >
                <FileText aria-hidden="true" />
                Draft referral letter
              </Button>
            ) : null}
            {canMessageRooms ? (
              <ButtonLink variant="secondary" to={`/board/messages/new?facility=${encodeURIComponent(specialist.id)}`}>
                <MessageCircle aria-hidden="true" />
                Message the rooms
              </ButtonLink>
            ) : null}
            <SavedFacilityButton facility={specialist} />
            <span className="ds-profile__actions-end">
              <Button
                variant="ghost"
                aria-expanded={correctionOpen}
                aria-controls="profile-correction"
                onClick={() => setCorrectionOpen((value) => !value)}
              >
                <Flag aria-hidden="true" />
                Report inaccuracy
              </Button>
            </span>
          </div>

          {correctionOpen ? (
            <div id="profile-correction" className="ds-profile__composer">
              <CorrectionForm citySlug={citySlug} slug={slug} onClose={() => setCorrectionOpen(false)} />
            </div>
          ) : null}

          {composerOpen && isGpMode ? (
            <div id="referral-composer" className="ds-profile__composer">
              <ReferralComposer input={referralTemplateInput} facilityId={specialist.id} />
            </div>
          ) : null}
        </article>
      </div>

      <div className="ds-split ds-profile__body">
        <div className="ds-stack" style={{ gap: 16 }}>
          {!isGpMode ? (
            <Panel aria-labelledby="patient-seeing-heading">
              <div className="ds-stack" style={{ gap: 8 }}>
                <p className="ds-eyebrow ds-eyebrow--teal">For patients</p>
                <h2 id="patient-seeing-heading" className="ds-title">Seeing {displayName}</h2>
              </div>
              <p className="ds-body">
                To claim a Medicare rebate you will usually need a referral from your GP. You can ask your GP to
                address it to {displayName}
                {practiceName ? ` at ${practiceName}` : ""}.
              </p>
              <p className="ds-body">
                Contact the practice to ask about fees, appointment times and whether telehealth is available.{" "}
                <Link className="ds-link" to={PATIENT_GUIDE.path}>How referrals, Medicare and costs work</Link>
              </p>
            </Panel>
          ) : null}

          {hasReferral && specialist.availability ? (
            <Panel aria-labelledby="referral-heading">
              <div className="ds-stack" style={{ gap: 8 }}>
                <p className="ds-eyebrow ds-eyebrow--teal">Referral instructions</p>
                <h2 id="referral-heading" className="ds-title">How to refer</h2>
              </div>
              <dl className="ds-dl ds-dl--rows">
                {specialist.availability.referralMethod ? (
                  <ReferralDetailRow label="Method" value={referralMethodLabel(specialist.availability.referralMethod) ?? ""} />
                ) : null}
                {specialist.availability.referralEmail ? (
                  <ReferralDetailRow label="Email" value={specialist.availability.referralEmail} copyable />
                ) : null}
                {specialist.availability.referralFax ? (
                  <ReferralDetailRow label="Fax" value={specialist.availability.referralFax} copyable />
                ) : null}
              </dl>
              {specialist.availability.referralNotes ? (
                <div className="ds-stack" style={{ gap: 6 }}>
                  <p className="ds-small"><strong>What to include</strong></p>
                  <p className="ds-body">{specialist.availability.referralNotes}</p>
                </div>
              ) : null}
            </Panel>
          ) : null}

          {notPublished.length > 0 ? (
            <Notice>
              <strong>Not yet published: {notPublished.join(", ")}.</strong> Contact the practice to confirm current
              referral details before referring.
            </Notice>
          ) : null}

          {isGpMode ? <WaitReportForm citySlug={citySlug} facilityId={specialist.id} /> : null}
        </div>

        <div className="ds-stack" style={{ gap: 16 }}>
          <Panel tight aria-labelledby="practice-details-heading">
            <h2 id="practice-details-heading" className="ds-title">Practice details</h2>
            <dl className="ds-dl ds-dl--rows">
              {practiceFlags.map((flag) => (
                <div key={flag.label}>
                  <dt>{flag.label}</dt>
                  <dd className={flag.tone === "muted" ? "ds-dl__muted" : undefined}>{flag.value}</dd>
                </div>
              ))}
            </dl>
            <p className="ds-fine">Public listing fields only. No unpublished availability is inferred.</p>
          </Panel>
          <ClaimProfileCta citySlug={citySlug} slug={slug} displayName={displayName} />
        </div>
      </div>

      {specialty ? (
        <SiblingSpecialists citySlug={citySlug} cityName={cityName} specialty={specialty} currentSlug={slug} />
      ) : null}

      <div style={{ marginTop: 16 }}>
        <BriefSubscribe sourcePath={`/directory/specialist/${slug}`} />
      </div>
    </div>
  );
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Outbound practice links carry UTM so practices see PulseBoard in their own analytics. */
function withDirectoryUtm(raw: string): string {
  try {
    const url = new URL(raw);
    if (!url.searchParams.has("utm_source")) {
      url.searchParams.set("utm_source", "pulseboard");
      url.searchParams.set("utm_medium", "directory");
    }
    return url.toString();
  } catch {
    return raw;
  }
}

/**
 * "Are you Dr X?" claim prompt. Sends one public lead (no patient data);
 * the team replies with a claim link from /admin/invites.
 */
function ClaimProfileCta({ citySlug, slug, displayName }: { citySlug: string; slug: string; displayName: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    try {
      await submitPublicLead(citySlug, {
        intent: "contact",
        email,
        name: name || undefined,
        message: `Claim request: profile ${slug}`,
        sourcePath: `/directory/specialist/${slug}`,
      });
      track("profile_claim_submit");
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <Panel tone="tint" tight aria-labelledby="profile-claim-heading" className="pb-profile-claim">
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">For practices</p>
        <h2 id="profile-claim-heading" className="ds-title">Are you {displayName} or their practice?</h2>
        <p className="ds-small">Claim this profile to add clinical interests, referral requirements and availability. Free for specialists and practice staff.</p>
      </div>
      {state === "success" ? (
        <Notice tone="success">Thanks. We will email you a claim link once we have checked the request.</Notice>
      ) : open ? (
        <form onSubmit={(event) => void handleSubmit(event)} className="ds-stack" style={{ gap: 12 }}>
          <TextField label="Work email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" placeholder="Optional" />
          {state === "error" ? <Notice tone="error">The request didn't send. Check the email address and try again.</Notice> : null}
          <div className="ds-cluster">
            <Button type="submit" disabled={state === "submitting"}>
              {state === "submitting" ? "Sending…" : "Request claim link"}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              track("profile_claim_click");
              setOpen(true);
            }}
          >
            Claim this profile
          </Button>
        </div>
      )}
    </Panel>
  );
}

/** "Other cardiologists in Canberra": up to five same-specialty profiles (report 28). */
function SiblingSpecialists({
  citySlug,
  cityName,
  specialty,
  currentSlug,
}: {
  citySlug: string;
  cityName: string;
  specialty: string;
  currentSlug: string;
}) {
  const specialtySlug = slugifySpecialty(specialty);
  const directory = usePublicDirectory(citySlug, { specialty: specialtySlug, limit: 12 });
  const siblings = (directory.data?.items ?? []).filter((item) => item.slug !== currentSlug).slice(0, 3);
  if (siblings.length === 0) return null;
  const plural = specialtyContent(specialtySlug)?.practitionerPlural ?? `${specialty} specialists`;

  return (
    <section className="ds-section" aria-labelledby="profile-siblings-heading">
      <SectionHead
        id="profile-siblings-heading"
        title={`Other ${plural} in ${cityName}`}
        actions={<ButtonLink variant="secondary" to={`/directory/${specialtySlug}`}>All {plural}</ButtonLink>}
      />
      <div className="ds-grid">
        {siblings.map((item) => <ListingCard key={item.id} facility={item} />)}
      </div>
    </section>
  );
}

/** Phone, address, website, email, fax and hours as one row of facts under the header. */
function ContactFacts({
  specialist,
  addressJson,
  addressLine,
  mapsUrl,
  onContact,
}: {
  specialist: Facility;
  addressJson: ExtendedAddressJson;
  addressLine: string | null;
  mapsUrl: string | null;
  onContact: (event: "phone_click" | "email_click" | "website_click") => void;
}) {
  const phone = cleanText(specialist.phone);
  const altPhone = cleanText(addressJson.phone_alt);
  const fax = cleanText(addressJson.fax);
  const email = cleanText(addressJson.email);
  const website = cleanText(specialist.website);
  const hours = cleanText(addressJson.hours);
  const hasAny = Boolean(phone || altPhone || fax || email || website || hours || addressLine);

  if (!hasAny) {
    return (
      <div className="ds-profile__facts">
        <div className="ds-fact">
          <span className="ds-eyebrow">Contact</span>
          <span className="ds-small">The public source doesn't list phone, email, website or address for this profile.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-profile__facts">
      {phone ? (
        <div className="ds-fact">
          <span className="ds-eyebrow">Phone</span>
          <a
            className="ds-num ds-fact__value"
            href={`tel:${phone.replace(/\s/g, "")}`}
            onClick={() => {
              track("contact_click", { channel: "phone" });
              onContact("phone_click");
            }}
          >
            {phone}
          </a>
          {altPhone ? <span className="ds-fine ds-num">Alt {altPhone}</span> : null}
        </div>
      ) : null}
      {addressLine ? (
        <div className="ds-fact">
          <span className="ds-eyebrow">Address</span>
          <span className="ds-fact__value">{addressLine}</span>
          {mapsUrl ? (
            <a className="ds-link ds-fine" href={mapsUrl} target="_blank" rel="noreferrer">
              Open in Maps
            </a>
          ) : null}
        </div>
      ) : null}
      {website || email ? (
        <div className="ds-fact">
          <span className="ds-eyebrow">Online</span>
          {website ? (
            <a
              className="ds-fact__value ds-link"
              href={withDirectoryUtm(website)}
              target="_blank"
              rel="noopener"
              onClick={() => {
                track("contact_click", { channel: "website" });
                onContact("website_click");
              }}
            >
              Practice website <ExternalLink aria-hidden="true" className="ds-inline-icon" />
            </a>
          ) : null}
          {email ? (
            <a
              className="ds-link ds-fine"
              href={`mailto:${email}`}
              onClick={() => {
                track("contact_click", { channel: "email" });
                onContact("email_click");
              }}
            >
              {email}
            </a>
          ) : null}
        </div>
      ) : null}
      {fax || hours ? (
        <div className="ds-fact">
          <span className="ds-eyebrow">{fax ? "Fax" : "Hours"}</span>
          <span className="ds-fact__value ds-num">{fax ?? hours}</span>
          {fax && hours ? <span className="ds-fine">{hours}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function ReferralDetailRow({ label, value, copyable = false }: { label: string; value: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <dt>{label}</dt>
      <dd className="ds-cluster" style={{ justifyContent: "flex-end", gap: 10 }}>
        <span className={copyable ? "ds-num" : undefined}>{value}</span>
        {copyable ? (
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={() => {
              void copyText(value).then((didCopy) => {
                setCopied(didCopy);
                if (didCopy) window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            <Copy aria-hidden="true" /> {copied ? "Copied" : "Copy"}
          </Button>
        ) : null}
      </dd>
    </div>
  );
}

function ProfileLoading() {
  return (
    <div className="ds-container" aria-label="Loading specialist profile">
      <div className="ds-page-head" style={{ paddingBottom: 24 }}>
        <div className="ds-skeleton" style={{ height: 18, width: 260 }} />
      </div>
      <div className="ds-skeleton" style={{ height: 360 }} />
    </div>
  );
}

function ProfileNotFound() {
  return (
    <div className="ds-container ds-container--narrow">
      <div className="ds-section">
        <EmptyState
          icon={<SearchX />}
          title="Specialist not found"
          action={<ButtonLink to="/directory">Browse the directory</ButtonLink>}
        >
          This listing may have been removed, or the link may be out of date.
        </EmptyState>
      </div>
    </div>
  );
}

type FormState = "idle" | "submitting" | "success" | "error";

function CorrectionForm({ citySlug, slug, onClose }: { citySlug: string; slug: string; onClose: () => void }) {
  const [state, setState] = useState<FormState>("idle");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setState("submitting");
    try {
      await submitCorrection(citySlug, slug, { reason, note: note || undefined, reporterEmail: email || undefined });
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="ds-stack" style={{ gap: 14, maxWidth: 640 }}>
      <h3 className="ds-title">Send a correction</h3>
      {state === "success" ? (
        <div className="ds-stack" style={{ gap: 12 }}>
          <Notice tone="success">Thank you. We will review your report within 2 business days.</Notice>
          <div><Button variant="secondary" onClick={onClose}>Close</Button></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="ds-stack" style={{ gap: 14 }}>
          <TextField
            label="What is incorrect?"
            required
            maxLength={200}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Wrong phone number, moved practice, retired…"
          />
          <TextAreaField label="More detail" maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} rows={3} placeholder="Optional" />
          <TextField label="Your email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Optional" />
          {state === "error" ? (
            <Notice tone="error">The report didn't send. Try again, or email corrections@pulseboard.com.au.</Notice>
          ) : null}
          <div className="ds-cluster">
            <Button type="submit" disabled={state === "submitting" || !reason.trim()}>
              {state === "submitting" ? "Sending…" : "Send correction"}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}

function sourceDisplayName(source: string | undefined): string {
  if (source === "cvlm-specialist-curated") return "PulseBoard Specialist Directory";
  if (source === "cvlm-referral-hub") return "PulseBoard Referral Hub";
  if (source === "apify-google-places") return "Google Places";
  if (source === "claimed-profile") return "Claimed profile";
  if (source === "practitioner-submitted") return "Practitioner submitted";
  if (!source) return "PulseBoard Directory";

  return source
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatSourceDate(raw: string | undefined): string | null {
  if (!raw) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);

  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatSpecialistDisplayName(name: string, title?: string | null): string {
  const cleanTitle = title?.trim();
  if (!cleanTitle) return name;

  return name.toLowerCase().startsWith(`${cleanTitle.toLowerCase()} `)
    ? name
    : `${cleanTitle} ${name}`;
}

function availabilityDisplay(availability: Availability | null): AvailabilitySummary {
  if (availability?.accepting === "yes") {
    return {
      label: acceptingLabel(availability.accepting),
      detail: waitBucketLabel(availability.waitBucket) ?? "Wait not published",
      tone: "available",
    };
  }

  if (availability?.accepting === "urgent_only") {
    return {
      label: acceptingLabel(availability.accepting),
      detail: waitBucketLabel(availability.waitBucket) ?? "Wait not published",
      tone: "limited",
    };
  }

  if (availability?.accepting === "no") {
    return {
      label: acceptingLabel(availability.accepting),
      detail: "Contact the practice before referring.",
      tone: "closed",
    };
  }

  return {
    label: "Unconfirmed",
    detail: "Availability has not been confirmed by the practice.",
    tone: "unconfirmed",
  };
}

function clinicalInterestList(addressJson: ExtendedAddressJson): string[] {
  return dedupeList([
    ...textList(addressJson.sub_specialties),
    ...textList(addressJson.clinical_interests),
    ...textList(addressJson.special_interests),
  ]);
}

function practiceFlagList(addressJson: ExtendedAddressJson): PracticeFlag[] {
  const flags: PracticeFlag[] = [];
  const billing = explicitBillingLabel(addressJson);
  const telehealth = explicitTelehealthLabel(addressJson.telehealth);
  const verified = verifiedStatus(addressJson);
  const hours = cleanText(addressJson.hours);

  flags.push({
    label: "Billing",
    value: billing ?? "Not published",
    tone: billing ? "teal" : "muted",
  });
  flags.push({
    label: "Telehealth",
    value: telehealth ?? "Not published",
    tone: telehealth === "Available" ? "teal" : telehealth === "Not available" ? "amber" : "muted",
  });
  flags.push({
    label: "Profile status",
    value: verified.label,
    tone: verified.verified ? "teal" : "muted",
  });
  flags.push({
    label: "Hours",
    value: hours ? "Published" : "Not published",
    tone: hours ? "teal" : "muted",
  });

  return flags;
}

function explicitBillingLabel(addressJson: ExtendedAddressJson): string | null {
  const explicit = addressJson.accepts_bulk_bill;
  if (explicit === true || explicit === "true" || explicit === "yes") return "Bulk-billing noted";
  if (explicit === false || explicit === "false" || explicit === "no") return "Private billing noted";
  return bulkBillingLabel(addressJson.bulk_billing);
}

function explicitTelehealthLabel(raw: boolean | string | null | undefined): string | null {
  if (raw === true) return "Available";
  if (raw === false) return "Not available";
  const value = cleanText(raw)?.toLowerCase();
  if (!value) return null;
  if (["yes", "true", "available"].includes(value)) return "Available";
  if (["no", "false", "unavailable"].includes(value)) return "Not available";
  return raw ? String(raw) : null;
}

function verifiedStatus(addressJson: ExtendedAddressJson): { label: string; verified: boolean } {
  const verified = truthy(addressJson.verified);
  const claimed = truthy(addressJson.claimed);

  if (verified) return { label: "Source-checked profile", verified: true };
  if (claimed || addressJson.source === "claimed-profile" || addressJson.source === "practitioner-submitted") {
    return { label: "Claimed profile", verified: false };
  }

  return { label: "Source-attributed listing", verified: false };
}

function truthy(raw: boolean | string | null | undefined): boolean {
  if (raw === true) return true;
  if (typeof raw !== "string") return false;
  return ["true", "yes", "1"].includes(raw.toLowerCase());
}

function profileAddressLine(addressJson: ExtendedAddressJson): string | null {
  const fullAddress = cleanText(addressJson.full_address);
  if (fullAddress) return fullAddress;

  const structured = [
    cleanText(addressJson.street),
    cleanText(addressJson.suburb),
    cleanText(addressJson.state),
    cleanText(addressJson.postcode),
  ]
    .filter(Boolean)
    .join(", ");

  return structured || null;
}

function hasReferralDetails(availability: Availability): boolean {
  return Boolean(
    availability.referralMethod ||
      availability.referralEmail ||
      availability.referralFax ||
      availability.referralNotes,
  );
}

function referralDetailsText(
  specialist: Facility,
  addressJson: ExtendedAddressJson,
  displayName: string,
): string {
  const availability = specialist.availability;
  const practice =
    cleanText(addressJson.practice_name) ?? specialist.practiceGroup?.name ?? null;
  const lines = [
    displayName,
    practice ? `Practice: ${practice}` : null,
    specialist.phone ? `Phone: ${specialist.phone}` : null,
    availability?.referralMethod
      ? `Referral method: ${referralMethodLabel(availability.referralMethod)}`
      : null,
    availability?.referralEmail
      ? `Referral email: ${availability.referralEmail}`
      : null,
    availability?.referralFax ? `Referral fax: ${availability.referralFax}` : null,
    availability?.referralNotes
      ? `What to include: ${availability.referralNotes}`
      : null,
  ];

  return lines.filter((line): line is string => Boolean(line)).join("\n");
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  }
}

function textList(raw: string[] | string | null | undefined): string[] {
  if (Array.isArray(raw)) {
    return raw.map(cleanText).filter((item): item is string => Boolean(item));
  }

  const value = cleanText(raw);
  if (!value) return [];

  return value
    .split(/\r?\n|;|\|/)
    .map(cleanText)
    .filter((item): item is string => Boolean(item));
}

function dedupeList(items: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of items) {
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }

  return result;
}

function cleanText(raw: string | number | boolean | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim();
  return value ? value : null;
}
